import React, { useState, useEffect } from "react";

import Container from "./container";
import { replaceMergeTagsInString, stringHasValueTag } from "../helper";

import { FormContext, iControlProps, iChoice } from "../types";

import { evaluates_as_hiddenV2 } from "../conditional-logic";
import { CheckboxV2 } from "@boi/react-components";

export type CheckboxProps = iControlProps & {
    context?: FormContext;
    choices: iChoice[];
    enableSelectAll?: boolean;
    field_deselect_all?: boolean;
    error?: string;
    form_field_tab_boi_selectable_checkbox_min?: string;
    form_field_tab_boi_selectable_checkbox_max?: string;
    inputs: { id: string; label: string; name: string }[];
    fieldId: string;
    type: string;
};

const loadDeselectChecked = ({
    value,
    choices,
    field_deselect_all,
    inputs
}: CheckboxProps) => {
    if (field_deselect_all) {
        const id = inputs[inputs.length - 1].id;
        const choice = choices[choices.length - 1];

        return choice.value === (value || {})[id];
    } else {
        return false;
    }
};

const Checkbox = (props: CheckboxProps) => {
    const [deselectChecked, setDeselectChecked] = useState(
        loadDeselectChecked(props)
    );

    const defaultInputs = props.choices.map(({ text: label, value }, i) => {
        let tempLabel = label;

        if (!stringHasValueTag(value)) {
            tempLabel = replaceMergeTagsInString(
                label,
                props.context ? props.context.getValues() : {}
            );
        }

        return {
            id: props.inputs[i]?.id || `${props.fieldId}.${i + 1}`,
            label: tempLabel
        };
    });

    const { inputs } = {
        ...props,
        inputs: defaultInputs
    };

    const visibleChoices =
        props.type === "conditionalCheckbox"
            ? props.choices.filter(choice => {
                  if (props.context && choice.conditionalLogic) {
                      if (
                          !evaluates_as_hiddenV2(
                              choice.conditionalLogic,
                              props.context.state
                          )
                      ) {
                          return choice;
                      } else {
                          return null;
                      }
                  } else {
                      return choice;
                  }
              })
            : props.choices;

    const enableSelectAll = props.enableSelectAll;
    const enableDeselectAll = props.field_deselect_all;

    const selectionCount = props.choices.reduce((res, element, index) => {
        const { id } = inputs[index];
        const choiceChecked = element.value === (props.value || {})[id];
        if (choiceChecked) {
            res++;
        }

        return res;
    }, 0);

    const maximumSelection = parseInt(
        props.form_field_tab_boi_selectable_checkbox_max ?? ""
    );
    const maximumSelectionReached =
        maximumSelection !== 0 && selectionCount === maximumSelection;

    const allSelected = () =>
        Object.values(props.value || {}).reduce(
            (r: number, i) => (i ? r + 1 : r),
            0
        ) ===
        (enableDeselectAll ? visibleChoices.length - 1 : visibleChoices.length);

    const toggleAll = () => {
        setDeselectChecked(false);
        if (props.onChange) {
            if (allSelected()) {
                props.name && props.context?.unsetValue(props.name);
            } else {
                const value = props.choices
                    .filter((choice, index) =>
                        enableDeselectAll && props.choices.length === index + 1
                            ? null
                            : choice
                    )
                    .reduce((r, c, i) => {
                        let hidden = false;
                        if (props.context && c.conditionalLogic) {
                            hidden = evaluates_as_hiddenV2(
                                c.conditionalLogic,
                                props.context.state
                            );
                        }
                        if (hidden) {
                            return { ...r };
                        }
                        return { ...r, [inputs[i].id]: c.value };
                    }, {});

                if (Object.keys(value).length) {
                    props.onChange && props.onChange(value);
                } else {
                    props.name && props.context?.unsetValue(props.name);
                }
            }
        }
    };

    useEffect(() => {
        props.choices.forEach((choice, index) => {
            if (props.type === "conditionalCheckbox") {
                if (props.context && choice.conditionalLogic) {
                    if (
                        evaluates_as_hiddenV2(
                            choice.conditionalLogic,
                            props.context.state
                        )
                    ) {
                        const { id } = inputs[index];
                        if (choice.value === (props.value || {})[id]) {
                            const { [id]: _, ...value } = props.value || {};
                            if (props.onChange) props.onChange(value);
                        }
                    }
                }
            }
        });
    });

    const inputChangedEvent = (choice: iChoice, id: string, index: number) => {
        const source = props.value || {};
        if (choice.value === source[id]) {
            //de-select
            const { [id]: _, ...value } = source;
            if (enableDeselectAll && props.choices.length === index + 1)
                setDeselectChecked(false);
            if (Object.keys(value).length) {
                props.onChange && props.onChange(value);
            } else {
                props.name && props.context?.unsetValue(props.name);
            }
        } else {
            //select
            let value;
            if (enableDeselectAll && props.choices.length === index + 1) {
                setDeselectChecked(true);
                value = {
                    [id]: choice.value
                };
            } else {
                if (enableDeselectAll)
                    delete source[inputs[props.choices.length - 1].id];
                value = {
                    ...source,
                    [id]: choice.value
                };
                setDeselectChecked(false);
            }
            props.onChange && props.onChange(value);
        }
    };

    const inputStyle = {
        display: "block",
        marginTop: "1rem",
        marginBottom: "1rem"
    };

    return (
        <Container {...props}>
            <div className="checkbox">
                {enableSelectAll && (
                    <CheckboxV2
                        labelProps={{
                            label: "Select all"
                        }}
                        isSelected={allSelected()}
                        onChange={toggleAll}
                        style={inputStyle}
                        key=".0"
                    />
                )}

                {props.choices.map((choice, index) => {
                    const { id, label } = inputs[index];

                    const choiceChecked =
                        choice.value === (props.value || {})[id];

                    let isDisabled = false;
                    if (!choiceChecked && maximumSelectionReached) {
                        isDisabled = true;
                    }

                    let hidden = false;
                    if (props.context && choice.conditionalLogic) {
                        hidden = evaluates_as_hiddenV2(
                            choice.conditionalLogic,
                            props.context.state
                        );
                    }

                    if (!hidden) {
                        return (
                            <CheckboxV2
                                labelProps={{
                                    label: label
                                }}
                                value={choice.value}
                                isSelected={
                                    enableDeselectAll &&
                                    props.choices.length === index + 1
                                        ? deselectChecked
                                        : choiceChecked
                                }
                                isDisabled={isDisabled}
                                name={props.name}
                                key={index}
                                style={inputStyle}
                                onChange={e =>
                                    inputChangedEvent(choice, id, index)
                                }
                            />
                        );
                    } else {
                        return null;
                    }
                })}
            </div>
        </Container>
    );
};

Checkbox.defaultProps = {
    choices: []
};

export default Checkbox;
